import harediLogo from '../../../../../assets/logos/haredi-logo.png';
import { Gender } from '../../../../../api/types/user/user-summary.type';
import { StudentProgramsWithCourses } from '../../../../../api/types/students/programs/student-programs-with-courses';

function toGregorianDate(date: Date): string {
  return date.toLocaleDateString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function genderPrefix(gender?: Gender): string {
  if (gender === 'female') return 'גב׳';
  if (gender === 'male') return 'מר';
  return '';
}

function studentVerb(gender?: Gender): string {
  if (gender === 'female') return 'הינה סטודנטית';
  if (gender === 'male') return 'הינו סטודנט';
  return 'הינו/ה סטודנט/ית';
}

export interface StudyCertificatePDFData {
  program: StudentProgramsWithCourses;
  studentName: string;
  studentId: string;
  studentGender?: Gender;
  displayProgramName: string;
  displayAcademicYear: string;
}

export interface StudyCertificateHTMLOptions {
  // Optional override for the logo source (e.g. base64 data URI). Falls back
  // to the bundled webpack URL when omitted. Word generation needs the logo
  // inlined so it is embedded in the .docx file.
  logoSrc?: string;
}

export const createStudyCertificateHTML = (
  data: StudyCertificatePDFData,
  options?: StudyCertificateHTMLOptions,
): string => {
  const {
    studentName,
    studentId,
    studentGender,
    displayProgramName,
    displayAcademicYear,
  } = data;

  const now = new Date();
  const gregDate = toGregorianDate(now);
  const prefix = genderPrefix(studentGender);
  const verb = studentVerb(studentGender);
  const programName = displayProgramName;
  const academicYear = displayAcademicYear;
  const logoSrc = options?.logoSrc ?? harediLogo;

  return `<!DOCTYPE html>
<html dir="rtl" lang="he">
<head>
  <meta charset="UTF-8"/>
  <link rel="preconnect" href="https://fonts.googleapis.com"/>
  <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Noto+Serif+Hebrew:wght@400;600;700&display=swap"/>
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    html, body {
      width: 794px;
      height: 1123px;
        font-family: 'David Libre', 'David CLM', 'David', serif;

      font-size: 14px;
      color: #1a1a1a;
      background: #fff;
      direction: rtl;
    }
    .page {
      width: 794px;
      height: 1123px;
      display: flex;
      flex-direction: column;
    }

    /* ── Header: contact left, logo right ── */

    .header-left {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;
    }
    .date-gregorian {
      font-size: 11px;
      font-family: Arial, sans-serif;
      color: #333;
      direction: ltr;
      margin-bottom: 4px;
    }
    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 18px 28px 14px 28px;
    }
    
    .contact-block {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      text-align: right;
      font-size: 10px;
      font-family: Arial, sans-serif;
      color: #444;
      line-height: 2;
    }
    .contact-block .site { font-weight: 700; color: #1f5c1f; }
    .logo { height: 88px; width: auto; }

    /* ── Dividers ── */
    .divider-gold {
      height: 3px;
      background: linear-gradient(to left, #7a5200, #c9a84c, #f5d060, #c9a84c, #7a5200);
    }
    .divider-green { height: 5px; background: #1f5c1f; margin-top: 2px; }

    /* ── Body ── */
    .body {
      flex: 1;
      padding: 48px 54px 20px 54px;
    }
    .salutation { font-size: 13px; margin-bottom: 22px; }
    .subject-row { margin-bottom: 36px; }
    .subject-label {
      font-size: 15px;
      font-weight: 700;
      text-decoration: underline;
      text-underline-offset: 3px;
    }
    .subject-value { font-size: 15px; font-weight: 700; }
    .cert-body {
      font-size: 14.5px;
      line-height: 2.4;
      text-align: right;
    }
    .bold { font-weight: 700; }
    .ltr  { direction: ltr; unicode-bidi: embed; display: inline-block; }

    /* ── Signature: pinned to bottom ── */
    .signature {
      margin-top: auto;
      padding: 20px 54px 40px 54px;
      font-size: 13px;
      line-height: 2.2;
      text-align: right;
    }

    .footer {
      text-align: center;
      font-size: 10px;
      color: #7f8c8d;
      border-top: 1px solid #dee2e6;
      padding: 12px 54px 24px 54px;
      font-family: Arial, sans-serif;
    }

    .footer a {
      color: #2980b9;
      text-decoration: none;
    }
  </style>
</head>
<body>
<div class="page">

  <!-- contact + date on the LEFT, logo on the RIGHT -->
  <div class="header">
    <div class="header-left">
      <div class="contact-block">
        <div>המרכז החרדי להכשרה מקצועית</div>
        <div class="site">www.charedicts.org.il</div>
        <!--<div>טל׳: 073-793-5805</div>
        <div>פקס: 073-793-5814</div>-->
        
        <div>${gregDate}</div>
      </div>
    </div>
    <img class="logo" src="${logoSrc}" alt="לוגו" />
  </div>

  <div class="divider-gold"></div>
  <div class="divider-green"></div>

  <div class="body">
    <p class="salutation">לכל המעוניין,</p>

    <div class="subject-row">
      <span class="subject-value">הנדון:</span>
      <span class="subject-value">אישור לימודים</span>
    </div>

    <p class="cert-body">
      הנני לאשר בזה כי
      <span class="bold">${studentName}</span>,
      ת.ז.<span class="bold ltr">${studentId}</span>,
      ${verb} במגמת
      <span class="bold">${programName}</span>
      ב״המרכז החרדי להכשרה מקצועית״ בשנת הלימודים
      <span class="bold">${academicYear}</span>.
    </p>

    <div class="signature">
      <div>בכבוד רב,</div>
      <div>מזכירות</div>
    </div>
  </div>

  <div class="footer">
    <div>
      מסמך זה נוצר באמצעות מערכת דירו
      <a href="https://www.diro-tech.com" target="_blank" rel="noopener noreferrer">www.diro-tech.com</a>
    </div>
  </div>

</div>
</body>
</html>`;
};
